<script setup>
import { computed } from "vue";
import VueMarkdown from "vue-markdown-render";

const props = defineProps({ data: { type: Object, default: () => ({}) } });

// Safe accessors with defaults
const mode = computed(() => props.data?.mode || "cta");

const heading = computed(() => props.data?.heading || "");
const body = computed(() => props.data?.body || "");
const showHeading = computed(() => props.data?.showHeading ?? true);
const showBody = computed(() => props.data?.showBody ?? true);

const image = computed(() => props.data?.image || null);
const imagePosition = computed(() => props.data?.imagePosition || "right");
const requestedMediaType = computed(() =>
  props.data?.mediaType === "video" ? "video" : "image"
);
const videoConfig = computed(() => props.data?.video || {});
const rawVideoUrl = computed(
  () => videoConfig.value?.url || props.data?.videoUrl || ""
);
function extractYouTubeId(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const trimmed = url.trim();
    const ytRegexes = [
      /(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtube\.com\/embed\/)([\w-]{11})/i,
      /youtu\.be\/([\w-]{11})/i,
    ];
    for (const regex of ytRegexes) {
      const match = trimmed.match(regex);
      if (match?.[1]) {
        return match[1];
      }
    }
    const urlObj = new URL(trimmed);
    if (urlObj.hostname.includes("youtube.com")) {
      const v = urlObj.searchParams.get("v");
      if (v && /^[\w-]{11}$/.test(v)) return v;
    }
  } catch (e) {
    // Invalid URL, ignore.
  }
  return "";
}
const videoId = computed(() => extractYouTubeId(rawVideoUrl.value));
const hasVideo = computed(() => !!videoId.value);
const hasImage = computed(() => !!image.value?.url);
const effectiveMediaType = computed(() => {
  if (requestedMediaType.value === "video") {
    if (hasVideo.value) return "video";
    if (hasImage.value) return "image";
    return "none";
  }
  if (hasImage.value) return "image";
  if (hasVideo.value) return "video";
  return "none";
});
const isVideo = computed(() => effectiveMediaType.value === "video");
const isImage = computed(() => effectiveMediaType.value === "image");
const hasMedia = computed(() => effectiveMediaType.value !== "none");
const videoEmbedUrl = computed(() =>
  isVideo.value ? `https://www.youtube.com/embed/${videoId.value}?rel=0&modestbranding=1` : ""
);

const align = computed(() => props.data?.align || "left");
const variantClass = computed(() => props.data?.variantClass || "");
const mediaFit = computed(() => props.data?.mediaFit || "cover");
const mediaMaxWidth = computed(() =>
  typeof props.data?.mediaMaxWidth === "string"
    ? props.data.mediaMaxWidth.trim()
    : ""
);
const mediaMaxHeight = computed(() =>
  typeof props.data?.mediaMaxHeight === "string"
    ? props.data.mediaMaxHeight.trim()
    : ""
);
const dimensionStyle = computed(() => {
  const style = {};
  if (mediaMaxWidth.value) style.maxWidth = mediaMaxWidth.value;
  if (mediaMaxHeight.value) style.maxHeight = mediaMaxHeight.value;
  return style;
});
const mediaWrapperClasses = computed(() => {
  const classes = ["relative", "flex", "w-full", "justify-center"];
  if (["cover", "fill"].includes(mediaFit.value)) {
    classes.push("items-center", "overflow-hidden");
  } else {
    classes.push("items-start");
  }
  if (hasMedia.value) {
    classes.push(
      imagePosition.value === "right" ? "md:order-2" : "md:order-1",
      "md:col-span-1"
    );
  }
  return classes;
});
const contentOrderClass = computed(() =>
  hasMedia.value
    ? imagePosition.value === "right"
      ? "md:order-1"
      : "md:order-2"
    : ""
);
const mediaClass = computed(() => {
  switch (mediaFit.value) {
    case "contain":
      return "object-contain max-w-full h-auto";
    case "scale-down":
      return "object-scale-down max-w-full h-auto";
    case "fill":
      return "object-fill w-full h-full";
    case "cover":
    default:
      return "object-cover w-full h-full";
  }
});
const mediaContainerClass = computed(() => {
  switch (mediaFit.value) {
    case "contain":
    case "scale-down":
      return "max-w-full mx-auto";
    case "fill":
    case "cover":
    default:
      return "w-full h-full";
  }
});

// imageWidth bounded 0..100
const sanitizedWidth = computed(() => {
  const w =
    typeof props.data?.imageWidth === "number" ? props.data.imageWidth : 60;
  if (Number.isNaN(w)) return 60;
  return Math.max(0, Math.min(100, Math.round(w)));
});
const gridStyle = computed(() =>
  hasMedia.value ? { "--image-col": `${sanitizedWidth.value}%` } : {}
);

// CTAs
const primary = computed(() => props.data?.primary || null);
const secondary = computed(() => props.data?.secondary || null);
const showPrimary = computed(() => props.data?.showPrimary ?? true);
const showSecondary = computed(() => props.data?.showSecondary ?? true);
const hasPrimary = computed(
  () => showPrimary.value && !!primary.value?.text && !!primary.value?.href
);
const hasSecondary = computed(
  () =>
    showSecondary.value && !!secondary.value?.text && !!secondary.value?.href
);

function targetRel(newTab) {
  return newTab ? { target: "_blank", rel: "noopener noreferrer" } : {};
}

/**
 * Overlap controls (for the “bleed” effect between columns)
 * data.overlap: number of pixels (md+ only). Default: 40
 */
const overlap = computed(() =>
  typeof props.data?.overlap === "number" ? props.data.overlap : 40
);

const overlapStyle = computed(() => ({
  "--overlap": `${overlap.value}px`,
}));

const overlapMediaClass = computed(() => {
  if (!hasMedia.value) return "";
  // pull media toward copy so they overlap slightly
  if (imagePosition.value === "right") return "md:ml-[calc(var(--overlap)*-1)]";
  return "md:mr-[calc(var(--overlap)*-1)]";
});

const overlapCopyClass = computed(() => {
  if (!hasMedia.value) return "";
  // pull copy toward media; keep it on top
  if (imagePosition.value === "right")
    return "relative z-10 md:mr-[calc(var(--overlap)*-1)]";
  return "relative z-10 md:ml-[calc(var(--overlap)*-1)]";
});
</script>

<template>
  <section v-if="mode === 'cta'" class="relative overflow-visible" :class="variantClass">
    <div class="relative grid gap-6 md:gap-10" :class="[
      hasMedia
        ? imagePosition === 'right'
          ? 'md:[grid-template-columns:minmax(0,1fr)_var(--image-col,50%)]'
          : 'md:[grid-template-columns:var(--image-col,50%)_minmax(0,1fr)]'
        : 'md:grid-cols-1',
      align === 'center' ? 'text-center' : 'text-left',
    ]" :style="gridStyle">
      <!-- Media -->
      <div v-if="hasMedia" :class="mediaWrapperClasses" :style="dimensionStyle">
        <template v-if="isImage">
          <img :src="image.url" :alt="image.alt || ''" :class="mediaClass" class="rounded-md" loading="lazy"
            decoding="async" />
        </template>
        <template v-else-if="isVideo">
          <div class="relative w-full overflow-hidden rounded-md"
            :class="mediaFit === 'contain' || mediaFit === 'scale-down' ? 'max-w-full mx-auto' : ''">
            <div class="w-full [aspect-ratio:16/9]">
              <iframe v-if="videoEmbedUrl" :src="videoEmbedUrl" title="Embedded video"
                class="absolute inset-0 h-full w-full rounded-md border-0" loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen />
            </div>
          </div>
        </template>
      </div>

      <!-- Copy -->
      <div class="flex flex-col gap-4" :class="[hasMedia ? ['md:col-span-1', contentOrderClass] : '']">
        <h3 v-if="showHeading" class="text-2xl font-semibold leading-tight">
          {{ heading }}
        </h3>

        <div v-if="showBody" class="prose max-w-none">
          <vue-markdown :source="body" />
        </div>

        <!-- CTAs -->
        <div v-if="hasPrimary || hasSecondary" class="mt-2 flex flex-wrap gap-3"
          :class="align === 'center' ? 'justify-center' : 'justify-start'">
          <a v-if="hasPrimary" :href="primary.href" v-bind="targetRel(primary.new_tab)"
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 transition">
            {{ primary.text }}
          </a>

          <a v-if="hasSecondary" :href="secondary.href" v-bind="targetRel(secondary.new_tab)"
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border hover:bg-muted transition">
            {{ secondary.text }}
          </a>
        </div>
      </div>
    </div>
  </section>

  <section v-else class="relative overflow-visible" :class="variantClass">
    <div class="relative grid gap-6 md:gap-10 md:items-start" :class="[
      hasMedia ? 'md:grid-cols-2' : 'md:grid-cols-1',
      align === 'center' ? 'text-center' : 'text-left',
    ]" :style="[gridStyle, overlapStyle]">
      <!-- Media -->
      <div v-if="hasMedia" :class="[mediaWrapperClasses, overlapMediaClass]" :style="dimensionStyle">
        <template v-if="isImage">
          <img :src="image.url" :alt="image.alt || ''" :class="mediaClass" loading="lazy" decoding="async" />
        </template>

        <template v-else-if="isVideo">
          <div class="relative w-full overflow-hidden" :class="mediaFit === 'contain' || mediaFit === 'scale-down'
              ? 'max-w-full mx-auto'
              : ''
            ">
            <div class="w-full [aspect-ratio:16/9]">
              <iframe v-if="videoEmbedUrl" :src="videoEmbedUrl" title="Embedded video"
                class="absolute inset-0 h-full w-full border-0" loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen />
            </div>
          </div>
        </template>
      </div>

      <!-- Copy -->
      <div class="flex flex-col" :class="[
        hasMedia ? ['md:col-span-1', contentOrderClass] : '',
        // drop the copy a bit lower than the media on md+
        'md:translate-y-12',
        // ensure the copy column can extend below the image without “matching” bottoms
        'md:pb-12',
        overlapCopyClass,
      ]">
        <div class="flex flex-col gap-4 bg-secondary p-6 text-foreground md:p-8">
          <h3 v-if="showHeading" class="text-2xl font-semibold leading-tight">
            {{ heading }}
          </h3>

          <div v-if="showBody" class="prose max-w-none">
            <vue-markdown :source="body" />
          </div>

          <!-- CTAs -->
          <div v-if="hasPrimary || hasSecondary" class="mt-2 flex flex-wrap gap-3"
            :class="align === 'center' ? 'justify-center' : 'justify-start'">
            <a v-if="hasPrimary" :href="primary.href" v-bind="targetRel(primary.new_tab)"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 transition">
              {{ primary.text }}
            </a>

            <a v-if="hasSecondary" :href="secondary.href" v-bind="targetRel(secondary.new_tab)"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border hover:bg-muted transition">
              {{ secondary.text }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
